import { generateUniqueId, type PieceModel } from '@doer/frontend-components/creativity-editor';
import type { AvatarsScene } from '../hooks/use-avatars-project';
import type { Cue } from './parse-vtt';
import type { AvatarsSubtitleStyle } from '../types/subtitles';

type ModelLayer = PieceModel['layers'][string];
type Dimension = { width: number; height: number };

const FPS = 30;
const MUSIC_DEFAULT_GAIN = 0.15;

/**
 * Ventana de fade-out de la música:
 *   - Con outro: arranca 1s antes de la placa y termina cuando termina la placa
 *     (duración total del fade = OUTRO_FADE_LEAD_MS + duración de la placa).
 *   - Sin outro: fadeamos los últimos DEFAULT_MUSIC_FADE_MS de la música.
 *
 * La rampa la aplica el runtime (`audio-sync.ts`) leyendo `content.fadeOutMs`.
 */
const OUTRO_FADE_LEAD_MS = 1000;
const DEFAULT_MUSIC_FADE_MS = 3000;

const DEFAULT_SUBTITLE_STYLE: AvatarsSubtitleStyle = {
  textFill: '#FFFFFF',
  fontFamily: 'Inter',
  fontSize: 65,
  fontWeight: 700,
  pillFill: '#5B4CFF',
  borderRadius: 25,
  paddingX: 35,
  paddingY: 20,
  shadow: {
    color: 'rgba(0, 0, 0, 1)',
    offsetX: 3,
    offsetY: 3,
    blur: 12
  },
  bottomRatio: 0.85
};

const buildSubtitleLayer = (
  cue: Cue,
  sceneStartMs: number,
  dimension: Dimension,
  style: AvatarsSubtitleStyle,
  label: string,
  videoId: string | undefined
): ModelLayer => ({
  id: generateUniqueId('pill'),
  kind: 'pill',
  visible: true,
  locked: false,
  label,
  // Lets smart-merge-piece.ts tell this apart from a pill the user added by hand in the editor —
  // both share kind: 'pill'. See docs/creativity-editor-modelo/bugs/1-pill-manual-desaparece-en-smart-merge.md.
  sceneDerived: true,
  // Explicit link to the scene's video — see docs/subtitulos-link-en-el-modelo.md. Read from the
  // model (not guessed from time overlap) by timeline/utils/attachment-graph.ts. A voice-only
  // scene has no video to attach to, so the cue (if any) stays unlinked.
  ...(videoId ? { timeAttach: { videoId } } : {}),
  left: Math.round(dimension.width / 2),
  top: Math.round(dimension.height * style.bottomRatio),
  scaleX: 1,
  scaleY: 1,
  rotation: 0,
  opacity: 1,
  originX: 'center',
  originY: 'center',
  start: sceneStartMs + Math.round(cue.start * 1000),
  duration: Math.max(1, Math.round((cue.end - cue.start) * 1000)),
  fit: 'fixed',
  ...(style.shadow ? { shadow: style.shadow } : {}),
  content: {
    text: cue.text,
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    textAlign: 'center',
    textFill: style.textFill,
    fill: style.pillFill,
    stroke: style.pillStroke ?? '#000000',
    strokeWidth: style.pillStrokeWidth ?? 0,
    borderRadius: style.borderRadius,
    paddingX: style.paddingX,
    paddingY: style.paddingY,
    minWidth: 0
  }
});

type VideoLayerOptions = {
  id: string;
  src: string;
  dimension: Dimension;
  start: number;
  duration: number;
  label: string;
  /** Scenes are the spine of the piece and can't be removed; the closing card can. */
  deletable: boolean;
  /**
   * Scenes carry the avatar's voice. Cards often bring their own audio, which fights the
   * music and the voiceover, so they play muted.
   */
  audioEnabled: boolean;
};

const buildVideoLayer = ({
  id,
  src,
  dimension,
  start,
  duration,
  label,
  deletable,
  audioEnabled
}: VideoLayerOptions): ModelLayer => ({
  id,
  kind: 'video',
  visible: true,
  locked: false,
  label,
  left: Math.round(dimension.width / 2),
  top: Math.round(dimension.height / 2),
  scaleX: 1,
  scaleY: 1,
  rotation: 0,
  opacity: 1,
  originX: 'center',
  originY: 'center',
  start,
  duration,
  fit: 'fixed',
  pinned: true,
  deletable,
  content: { src, intrinsicDuration: duration, audioEnabled }
});

const buildVoiceoverLayer = (scene: AvatarsScene, start: number): ModelLayer => ({
  id: generateUniqueId('audio'),
  kind: 'audio',
  visible: true,
  locked: false,
  label: `Voz — escena ${scene.index + 1}`,
  start,
  duration: scene.durationMs,
  fit: 'fixed',
  content: { source: 'tts', src: scene.audioUrl!, script: scene.script }
});

const buildMusicLayer = (
  music: { trackId: string; trackName: string; audioUrl: string },
  duration: number,
  fadeOutMs?: number
): ModelLayer => ({
  id: generateUniqueId('audio'),
  kind: 'audio',
  visible: true,
  locked: false,
  label: music.trackName,
  start: 0,
  duration,
  fit: 'fixed',
  content: {
    source: 'library',
    origin: 'studio',
    src: music.audioUrl,
    trackId: music.trackId,
    trackName: music.trackName,
    gain: MUSIC_DEFAULT_GAIN,
    ...(fadeOutMs !== undefined ? { fadeOutMs } : {})
  }
});

type BuildScenesModelOptions = {
  projectId: string;
  scenes: AvatarsScene[];
  dimension: Dimension;
  outro?: { videoUrl: string; durationMs: number };
  music?: { trackId: string; trackName: string; audioUrl: string };
  subtitleStyle?: AvatarsSubtitleStyle;
};

/** Compiles the finished scenes into the piece the editor opens: videos back to back, subtitles on top. */
export const buildScenesModel = ({
  projectId,
  scenes,
  dimension,
  outro,
  music,
  subtitleStyle
}: BuildScenesModelOptions): PieceModel => {
  const style = subtitleStyle ?? DEFAULT_SUBTITLE_STYLE;

  // In chronological order; the stack is its mirror (see layerOrder below).
  const visual: ModelLayer[] = [];
  const audio: ModelLayer[] = [];
  let cursorMs = 0;

  for (const scene of scenes) {
    const label = `Escena ${scene.index + 1}`;
    // Known before the video layer is built, so subtitles can link to it explicitly.
    const videoId = scene.videoUrl ? generateUniqueId('video') : undefined;

    for (const cue of scene.subtitleCues ?? []) {
      visual.push(buildSubtitleLayer(cue, cursorMs, dimension, style, `Subtítulo — ${label.toLowerCase()}`, videoId));
    }

    if (scene.videoUrl && videoId) {
      visual.push(
        buildVideoLayer({
          id: videoId,
          src: scene.videoUrl,
          dimension,
          start: cursorMs,
          duration: scene.durationMs,
          label,
          deletable: false,
          audioEnabled: true
        })
      );
    } else if (scene.audioUrl) {
      // Voice-only scene: nothing on the canvas for that window, just the voice on the timeline.
      audio.push(buildVoiceoverLayer(scene, cursorMs));
    }

    cursorMs += scene.durationMs;
  }

  if (outro) {
    visual.push(
      buildVideoLayer({
        id: generateUniqueId('video'),
        src: outro.videoUrl,
        dimension,
        start: cursorMs,
        duration: outro.durationMs,
        label: 'Placa de cierre',
        deletable: true,
        audioEnabled: false
      })
    );
  }

  const duration = cursorMs + (outro?.durationMs ?? 0);
  if (music && duration > 0) {
    const fadeOutMs = outro ? outro.durationMs + OUTRO_FADE_LEAD_MS : DEFAULT_MUSIC_FADE_MS;
    audio.push(buildMusicLayer(music, duration, Math.min(fadeOutMs, duration)));
  }

  const layers: PieceModel['layers'] = {};
  for (const layer of [...visual, ...audio]) layers[layer.id] = layer;

  return {
    id: projectId,
    width: dimension.width,
    height: dimension.height,
    type: 'video',
    fps: FPS,
    duration,
    background: { kind: 'color', color: '#FFFFFF' },
    layers,
    // Back to front. Reversing the chronological order puts each scene's subtitles above its
    // video, and makes the timeline (which mirrors the stack) read as a staircase in time.
    // Audio stays out: it isn't part of the visual stack.
    layerOrder: visual.map(l => l.id).reverse()
  };
};
